import React from 'react';
import { withRouter } from 'react-router';
import queryString from 'query-string';
import debounce from 'lodash/debounce';
import { mapAfter } from 'apollo-relay/update-query';
import { Query } from '@apollo/client/react/components';
import { FormattedMessage } from 'react-intl';
import Styled from 'styled';

import LoadingProgress from 'lib/react-router/loading-progress';
import { RouteWithSearchRenderProps } from 'lib/react-router/route-with-search';
import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';
import { InterscrollerPlaceholder } from 'lib/google/dfp/ad-units/interscroller';

import ErrorPage from 'four-nets/errors/error-page';
import OrderingSelect from 'four-nets/ui-v2/ordering-select/ordering-select';
import FetchMoreButton, { adjacentHrefsFromCursor } from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import OffsetPager from 'four-nets/ui-v2/pager/offset-pager';

import searchInputStyles from './search-input-styles.scss';
import defaultStyles from './all-groups.scss';
import Group from './group/group';
import allGroupsQuery, { AllGroupsQuery, AllGroupsQueryVariables } from './all-groups.graphql';
import { GroupOrderingFilterEnum } from './all-groups.graphql.types';

export interface Props extends RouteWithSearchRenderProps<{}, { offset: number }> {
    styles: typeof defaultStyles;
}

export interface State {
    ordering: string;
    groupName: string;
}

const FIRST = 8;
const PAGE_SIZE = 16;

class AllGroups extends React.Component<Props, State> {
    searchGroupWithDebounce = debounce((searchGroup: string) => {
        this.props.history.push({
            pathname: this.props.location.pathname,
            search: `?${queryString.stringify({
                ...this.locationSearch(),
                groupName: searchGroup,
            })}`,
        });
        this.setState({ groupName: searchGroup });
    }, 300);
    constructor(props: Props) {
        super(props);
        this.state = queryString.parse(this.props.location.search);
    }

    changeOrdering(newOrdering: string) {
        this.props.history.push({
            pathname: this.props.location.pathname,
            search: `?${queryString.stringify({
                ...this.locationSearch(),
                ordering: newOrdering,
            })}`,
        });
        this.setState({ ordering: newOrdering });
    }

    locationSearch() {
        return queryString.parse(this.props.location.search);
    }

    render() {
        const { styles, search } = this.props;
        const { offset } = search;

        let after: string | undefined;
        if (offset) {
            after = btoa(`arrayconnection:${parseInt(offset as any, 10) - 1}`);
        }

        return (
            <div className={styles.container}>
                <Query<AllGroupsQuery, AllGroupsQueryVariables>
                    query={allGroupsQuery}
                    variables={{
                        first: after ? FIRST * 2 : FIRST,
                        orderBy: this.state.ordering
                            ? (this.state.ordering as GroupOrderingFilterEnum)
                            : GroupOrderingFilterEnum.MEMBERS_COUNT_DESC,
                        name: this.state.groupName,
                        after,
                    }}
                    fetchPolicy='cache-first'
                >
                    {({ loading, error, data, fetchMore }) => {
                        const pageInfo = data && data.groups && data.groups.pageInfo;

                        let hasNextPage, endCursor: string | null | undefined;
                        if (pageInfo) {
                            ({ endCursor, hasNextPage } = pageInfo);
                        }

                        const [endCursorHref, prevCursorHref] = adjacentHrefsFromCursor({
                            endCursor,
                            pathname: this.props.location.pathname,
                            pageSize: FIRST,
                        });

                        let nextOffset;
                        if (endCursor) {
                            nextOffset = parseInt(atob(endCursor).split(':')[1], 10) + 1;
                        } else {
                            nextOffset = offset;
                        }

                        return (
                            <React.Fragment>
                                <div className={styles.header}>
                                    <h2 className={styles.titleName}>
                                        <FormattedMessage id='GROUPS_ALL_GROUPS' />
                                    </h2>
                                    {data ? <span className={styles.groupCount}>({data.groupCount})</span> : null}
                                    <div className={styles.inputContainer}>
                                        <OrderingSelect
                                            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                                                this.changeOrdering(event.target.value)
                                            }
                                            value={
                                                Object.keys(GroupOrderingFilterEnum).some(key => {
                                                    return key === this.state.ordering;
                                                })
                                                    ? this.state.ordering
                                                    : GroupOrderingFilterEnum.MEMBERS_COUNT_DESC
                                            }
                                            className={styles.groupOrderSelect}
                                        >
                                            {Object.keys(GroupOrderingFilterEnum).map(key => (
                                                <FormattedMessage
                                                    key={key}
                                                    id='GROUPS_ORDERING_FILTER'
                                                    values={{ value: key }}
                                                >
                                                    {message => <option value={key}>{message}</option>}
                                                </FormattedMessage>
                                            ))}
                                        </OrderingSelect>
                                    </div>
                                </div>
                                <LoadingProgress loading={loading}>
                                    <ErrorPage error={error}>
                                        {data && data.groups ? (
                                            <React.Fragment>
                                                <ul className={styles.groups}>
                                                    {data.groups.edges && data.groups.edges
                                                        ? data.groups.edges.map((edge, index) =>
                                                              edge && edge.node ? (
                                                                  <React.Fragment key={index}>
                                                                      <li className={styles.group} key={index}>
                                                                          <Group {...edge.node} key={index} />
                                                                      </li>
                                                                      {index === 1 ? (
                                                                          <li
                                                                              className={styles.interscroller}
                                                                              key='interscrollerPlaceholder'
                                                                          >
                                                                              <InterscrollerPlaceholder />
                                                                          </li>
                                                                      ) : null}
                                                                  </React.Fragment>
                                                              ) : null
                                                          )
                                                        : null}
                                                </ul>
                                                {hasNextPage ? (
                                                    <FetchMoreButton
                                                        style={{ button: styles.loadMoreButton }}
                                                        disabled={loading}
                                                        href={endCursorHref}
                                                        prevHref={prevCursorHref}
                                                        onClick={() => {
                                                            return fetchMore({
                                                                variables: {
                                                                    after: endCursor,
                                                                    first: FIRST * 2,
                                                                },
                                                                updateQuery: (
                                                                    previousResult = {} as AllGroupsQuery,
                                                                    { fetchMoreResult = {} as AllGroupsQuery }
                                                                ) => {
                                                                    return {
                                                                        ...previousResult,
                                                                        ...fetchMoreResult,
                                                                        groups: mapAfter(
                                                                            previousResult.groups,
                                                                            fetchMoreResult.groups
                                                                        ),
                                                                    };
                                                                },
                                                            });
                                                        }}
                                                    />
                                                ) : null}
                                                {hasNextPage || offset ? (
                                                    <OffsetPager
                                                        style={{ nav: styles.nav }}
                                                        pageSize={PAGE_SIZE}
                                                        offset={nextOffset}
                                                    />
                                                ) : null}
                                            </React.Fragment>
                                        ) : null}
                                    </ErrorPage>
                                </LoadingProgress>
                            </React.Fragment>
                        );
                    }}
                </Query>
            </div>
        );
    }
}

export default Styled(defaultStyles)(withRouter(AllGroups));
